import { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

/**
 * Scrollable TX/RX view of the serial traffic captured by serialClient.
 * Autoscrolls its OWN container (never the page) so live output can't yank the
 * whole view down. Shared by the connected "Serial Log" tab and the admin
 * serial-log panel on the connect screen.
 */
export default function SerialLogPanel({ entries, maxHeight = 480 }) {
  const boxRef = useRef(null);
  useEffect(() => {
    const box = boxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [entries.length]);

  return (
    <Box
      ref={boxRef}
      sx={{
        maxHeight,
        overflow: 'auto',
        bgcolor: '#1e1e1e',
        color: '#d4d4d4',
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        lineHeight: 1.6,
        p: 1.5,
        borderRadius: 1,
      }}
    >
      {entries.length === 0 ? (
        <Box component="span" sx={{ color: '#808080', fontStyle: 'italic' }}>
          Waiting for serial traffic&#8230;
        </Box>
      ) : (
        entries.map((entry, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1 }}>
            <Box component="span" sx={{ color: '#6a9955', minWidth: 72, flexShrink: 0 }}>
              {entry.timestamp.toLocaleTimeString('en-US', {
                hour12: false,
                fractionalSecondDigits: 3,
              })}
            </Box>
            <Box
              component="span"
              sx={{
                color: entry.direction === 'tx' ? '#569cd6' : '#ce9178',
                minWidth: 24,
                flexShrink: 0,
                fontWeight: 'bold',
              }}
            >
              {entry.direction === 'tx' ? 'TX' : 'RX'}
            </Box>
            <Box
              component="span"
              sx={{
                color: entry.type === 'binary' ? '#808080' : '#d4d4d4',
                fontStyle: entry.type === 'binary' ? 'italic' : 'normal',
                wordBreak: 'break-all',
              }}
            >
              {entry.data}
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
}

SerialLogPanel.propTypes = {
  entries: PropTypes.array.isRequired,
  maxHeight: PropTypes.number,
};
